import { Box, IconButton, Typography, useTheme } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FiX } from "react-icons/fi";
import { LuImagePlus } from "react-icons/lu";
import config from "../../../config";

const ACCEPTED_TYPES = ".jpg,.jpeg,.png,.webp";
const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024;

/**
 * Multi-photo upload grid for the Create tab's Photo section.
 * Photos are stored via the same emission_reference_files mechanism used by
 * Evidence Repository (scope="DPP Evidence", is_photo=true), so every upload
 * here also shows up there automatically. The ✕ here only sets
 * hidden_in_photo_section — Evidence Repository is the only place a photo
 * can actually be deleted.
 */
const DppPhotoGallery = ({ entityId, idColumnName = "product_id", scope = "DPP Evidence", refreshSignal, onPhotosChanged = () => {} }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const fileInputRef = useRef(null);
  const [photos, setPhotos] = useState([]);
  const [uploadingCount, setUploadingCount] = useState(0);

  const fetchPhotos = useCallback(() => {
    if (!entityId) {
      setPhotos([]);
      return;
    }
    fetch(`${config.apiUrl}/api/fetch_emission_reference_files?ID=${entityId}`)
      .then((res) => res.json())
      .then((data) =>
        setPhotos(
          data.filter((f) => f.scope === scope && f.is_photo && !f.hidden_in_photo_section)
        )
      )
      .catch(() => {});
  }, [entityId, scope]);

  useEffect(() => fetchPhotos(), [fetchPhotos, refreshSignal]);

  const handleFileChange = useCallback(async (e) => {
    const files = Array.from(e.target.files);
    e.target.value = null;
    if (!files.length || !entityId) return;

    const oversized = files.filter((f) => f.size > MAX_FILE_SIZE_BYTES);
    if (oversized.length > 0) return;

    setUploadingCount((c) => c + files.length);
    try {
      const formData = new FormData();
      files.forEach((f) => formData.append("files", f));
      formData.append("rowID", entityId);
      formData.append("scope", scope);
      formData.append("IDColumnName", idColumnName);
      formData.append("isPhoto", "true");

      const res = await fetch(`${config.apiUrl}/api/upload_emission_reference_files`, { method: "POST", body: formData });
      if (!res.ok) throw new Error("Upload failed");
      fetchPhotos();
      onPhotosChanged();
    } catch (err) {
      console.error("[DppPhotoGallery] Upload error:", err);
    } finally {
      setUploadingCount((c) => Math.max(0, c - files.length));
    }
  }, [entityId, idColumnName, scope, fetchPhotos, onPhotosChanged]);

  const handleHide = useCallback(async (fileId) => {
    setPhotos((prev) => prev.filter((p) => p.id !== fileId));
    try {
      await fetch(`${config.apiUrl}/api/hide_emission_reference_file`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ file_id: fileId }),
      });
      onPhotosChanged();
    } catch (err) {
      console.error("[DppPhotoGallery] Hide error:", err);
      fetchPhotos();
    }
  }, [fetchPhotos, onPhotosChanged]);

  const tileSx = {
    width: 140,
    height: 140,
    borderRadius: 1,
    position: "relative",
    overflow: "hidden",
    flexShrink: 0,
  };

  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
      {photos.map((photo) => (
        <Box key={photo.id} sx={{ ...tileSx, border: `1px solid ${isDark ? "#3f3f46" : "#d1d5db"}` }}>
          <img src={photo.url} alt={photo.name} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
          <IconButton
            onClick={() => handleHide(photo.id)}
            size="small"
            sx={{
              position: "absolute",
              top: 4,
              right: 4,
              width: 22,
              height: 22,
              backgroundColor: "rgba(0,0,0,0.55)",
              color: "#fff",
              "&:hover": { backgroundColor: "rgba(0,0,0,0.75)" },
            }}
          >
            <FiX size={14} />
          </IconButton>
        </Box>
      ))}
      {Array.from({ length: uploadingCount }).map((_, i) => (
        <Box
          key={`uploading-${i}`}
          sx={{
            ...tileSx,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: isDark ? "#27272a" : "#f9fafb",
            border: `2px dashed ${isDark ? "#3f3f46" : "#d1d5db"}`,
          }}
        >
          <Typography sx={{ fontSize: 12, color: isDark ? "#94a3b8" : "#6b7280", fontFamily: "Poppins" }}>{t("Uploading...")}</Typography>
        </Box>
      ))}
      <Box
        onClick={() => entityId && fileInputRef.current?.click()}
        sx={{
          ...tileSx,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 0.5,
          cursor: entityId ? "pointer" : "not-allowed",
          backgroundColor: isDark ? "#27272a" : "#f9fafb",
          border: `2px dashed ${isDark ? "#3f3f46" : "#d1d5db"}`,
          "&:hover": entityId ? { borderColor: isDark ? "#4ade80" : "#10b981" } : {},
        }}
      >
        <LuImagePlus size={22} color={isDark ? "#94a3b8" : "#6b7280"} />
        <Typography sx={{ fontSize: 12, color: isDark ? "#94a3b8" : "#6b7280", fontFamily: "Poppins", textAlign: "center", px: 1 }}>
          {t("Add Photo")}
        </Typography>
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPTED_TYPES}
          multiple
          onChange={handleFileChange}
          style={{ display: "none" }}
        />
      </Box>
    </Box>
  );
};
export default DppPhotoGallery;
